import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { Log } from '@shared/debug/log';
import { ModalComponent } from '@shared/modal/modal.component';
import { SharedModule } from '@shared/shared.module';
import { ActionLogDirective } from '@user/service/logging/actionlog.directive';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { TooltipDirective } from '@shared/tooltip/tooltip.directive';
import { ArrayUtils } from '@shared/util/array-utils';
import { LocaleUtils } from '@shared/util/locale-utils';
import { ArticleVO } from '../../model/vo/article.vo';
import { ArticleExportDataService } from '../../service/article-export-data.service';
import { ArticlePreviewUtils } from '../../util/article-preview.utils';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { ArticleTypeIds } from '@article/constants/article-type-ids';

export interface CddArticleData {
  articleNo: string,
  publicGlobal: boolean,
  isSelected: boolean,
  type: number,
  cddAvailable: boolean,
  label: string,
}

@Component({
  selector: 'data-center-export',
  templateUrl: 'data-center-export.component.html',
  imports: [
    TranslateModule,
    SharedModule,
    FormsModule,
    ScrollingModule,
    SvgIconComponent,
    TooltipDirective,
    ActionLogDirective,
    ModalComponent
  ],
})
export class DataCenterExportComponent implements OnInit {
  private readonly ARTICLE_DISPLAY_NAME_SEPARATOR: string = ' | ';

  public articles?: (ArticleVO | ArticleWithTechnicalPropertiesDTO)[];

  public cddArticles = signal<CddArticleData[]>([]);

  public isRequestDataDisabled = computed<boolean>(() => ArrayUtils.isEmpty(this.getSelectedArticleNumbers()));

  private readonly getSelectedArticleNumbers = computed<string[]>(() => {
    return this.cddArticles()
      .filter((article: CddArticleData) => article.isSelected && this.isCddAvailableForArticle(article))
      .map((selectedArticle: CddArticleData) => selectedArticle.articleNo);
  });

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly articleExportDataService: ArticleExportDataService,
    private readonly projectModel: ProjectModel,
    private readonly alertHelper: AlertHelper,
  ) {}

  public ngOnInit() {
    this.setCddArticles();
  }

  private setCddArticles(): void {
    if (!Log.assertExists(this.articles, 'articles')) {
      return;
    }
    const cddArticles = this.articles.map((article: ArticleVO | ArticleWithTechnicalPropertiesDTO) => {

      const cddArticleData: CddArticleData = {
        articleNo: article.articleNo,
        publicGlobal: article.publicGlobal,
        isSelected: false,
        type: 'articleType' in article ? article.articleType : article.type.id,
        cddAvailable: false,
        label: this.getArticleLabel(article)
      };

      cddArticleData.isSelected = cddArticleData.cddAvailable = this.isCddAvailableForArticle(cddArticleData);

      return cddArticleData;
    });

    this.cddArticles.set(cddArticles);
  }

  private getArticleLabel(article: ArticleVO | ArticleWithTechnicalPropertiesDTO): string {
    const displayName: string =
      article instanceof ArticleVO
        ? LocaleUtils.getValueByLocaleChain(article.displayName, this.localeResourceManager.getLocaleChain())
        : article.displayName;

    return displayName ? article.articleNo + this.ARTICLE_DISPLAY_NAME_SEPARATOR + displayName : article.articleNo;
  }

  public showArticleTechnicalInfo(article: CddArticleData): void {
    ArticlePreviewUtils.openShopLink(article.articleNo, this.localeResourceManager);
  }

  public toggleAllArticlesSelection(checked: boolean): void {
    this.cddArticles.update(articles =>
      articles.map(article => this.isCddAvailableForArticle(article)
        ? { ...article, isSelected: checked }
        : article
      )
    );
  }

  public toggleItemSelection(item: CddArticleData, event: MouseEvent): void {
    if (!this.isCddAvailableForArticle(item)) {
      return;
    }
    event.preventDefault();
    this.cddArticles.update(articles =>
      articles.map(article => article === item
        ? { ...article, isSelected: !article.isSelected }
        : article
      )
    );
  }

  public isCddAvailableForArticle(article: CddArticleData): boolean {
    return !!article.publicGlobal && article.type !== ArticleTypeIds.LABELING;
  }

  public async requestData(): Promise<void> {
    const url = await firstValueFrom(this.articleExportDataService.exportToDataCenter({
      projectId: this.projectModel.currentProject.id,
      articleNumbers: this.getSelectedArticleNumbers(),
    }));

    this.alertHelper.showAlert(
      'dataCenterExport.request_datacenter.title',
      'dataCenterExport.request_datacenter.message',
      undefined,
      [AlertButton.NEXT, AlertButton.CANCEL],
      'info',
      null,
      true,
      true)
      .then((answer: AlertButton) => {
        if (answer == AlertButton.NEXT) {
          window.open(url, '_blank');
        }
      });

    this.bsModalRef.hide();
  }

  public close(): void {
    this.bsModalRef.hide();
  }
}
